package dk.smark.demo.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.launch
import kotlin.math.*

@Composable
fun TileMapView(
    center: LatLon,
    zoom: Int = 15,
    markers: List<MapMarker> = emptyList(),
    polylines: List<MapPolyline> = emptyList(),
    commands: SharedFlow<MapCommand>? = null,
    modifier: Modifier = Modifier,
) {
    val tileSize = 256

    var panOffset by remember { mutableStateOf(Offset.Zero) }
    var currentZoom by remember { mutableIntStateOf(zoom) }

    val scope = rememberCoroutineScope()
    val tiles = remember { mutableStateMapOf<Triple<Int, Int, Int>, androidx.compose.ui.graphics.ImageBitmap>() }

    // Listen to zoom commands from the controller
    LaunchedEffect(commands) {
        commands?.collect { command ->
            when (command) {
                is MapCommand.ZoomIn -> {
                    val newZoom = (currentZoom + 1).coerceIn(1, 19)
                    if (newZoom != currentZoom) {
                        panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
                        currentZoom = newZoom
                    }
                }
                is MapCommand.ZoomOut -> {
                    val newZoom = (currentZoom - 1).coerceIn(1, 19)
                    if (newZoom != currentZoom) {
                        panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
                        currentZoom = newZoom
                    }
                }
                else -> {}
            }
        }
    }

    // Track previous centroid and span for manual pinch detection
    var lastCentroid by remember { mutableStateOf<Offset?>(null) }
    var lastSpan by remember { mutableStateOf<Float?>(null) }

    Canvas(
        modifier = modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent(PointerEventPass.Main)

                        // Always consume all changes to prevent propagation
                        event.changes.forEach { it.consume() }

                        val pointers = event.changes.filter { it.pressed }

                        if (pointers.isEmpty()) {
                            lastCentroid = null
                            lastSpan = null
                            continue
                        }

                        if (pointers.size == 1) {
                            // Single finger pan
                            val current = pointers[0].position
                            val previous = pointers[0].previousPosition
                            val delta = current - previous
                            if (lastCentroid != null) {
                                panOffset -= delta
                            }
                            lastCentroid = current
                            lastSpan = null
                        } else {
//                            // Multi-finger pinch: use first two pointers
//                            val p0 = pointers[0].position
//                            val p1 = pointers[1].position
//                            val centroid = (p0 + p1) / 2f
//                            val span = (p0 - p1).getDistance()
//
//                            val prevSpan = lastSpan
//                            val prevCentroid = lastCentroid
//
//                            if (prevSpan != null && prevCentroid != null) {
//                                // Pan by centroid movement
//                                panOffset -= (centroid - prevCentroid)
//
//                                // Zoom by span ratio
//                                val zoomDelta = span / prevSpan
//                                val newZoom = (currentZoom + when {
//                                    zoomDelta > 1.05f -> 1
//                                    zoomDelta < 0.95f -> -1
//                                    else -> 0
//                                }).coerceIn(1, 19)
//
//                                if (newZoom != currentZoom) {
//                                    panOffset = rescalePanOffset(panOffset, currentZoom, newZoom, center)
//                                    currentZoom = newZoom
//                                }
//                            }
//
//                            lastCentroid = centroid
//                            lastSpan = span
                        }
                    }
                }
            }
    ) {
        val canvasW = size.width
        val canvasH = size.height

        val (centerPxF, centerPyF) = TileMath.latLonToPixel(center.lat, center.lon, currentZoom)
        val centerPx = centerPxF + panOffset.x
        val centerPy = centerPyF + panOffset.y

        val originX = centerPx - canvasW / 2
        val originY = centerPy - canvasH / 2

        val tileX0 = (originX / tileSize).toInt() - 1
        val tileY0 = (originY / tileSize).toInt() - 1
        val tileX1 = ((originX + canvasW) / tileSize).toInt() + 1
        val tileY1 = ((originY + canvasH) / tileSize).toInt() + 1
        val maxTile = 2.0.pow(currentZoom).toInt() - 1

        for (tx in tileX0..tileX1) {
            for (ty in tileY0..tileY1) {
                if (tx < 0 || ty < 0 || tx > maxTile || ty > maxTile) continue

                val key = Triple(tx, ty, currentZoom)
                val bitmap = tiles[key]
                if (bitmap == null) {
                    scope.launch(Dispatchers.Main) {
                        loadTile(tx, ty, currentZoom)?.let { tiles[key] = it }
                    }
                } else {
                    val drawX = (tx * tileSize - originX).toInt()
                    val drawY = (ty * tileSize - originY).toInt()
                    drawImage(
                        image = bitmap,
                        dstOffset = IntOffset(drawX, drawY),
                        dstSize = IntSize(tileSize, tileSize),
                    )
                }
            }
        }

        markers.forEach { marker ->
            val (mpx, mpy) = TileMath.latLonToPixel(marker.position.lat, marker.position.lon, currentZoom)
            val sx = (mpx - originX).toFloat()
            val sy = (mpy - originY).toFloat()
            drawCircle(color = if (marker.isSelected) Color.Red else Color.Blue, radius = 14f, center = Offset(sx, sy))
            drawCircle(color = Color.White, radius = 7f, center = Offset(sx, sy))
        }

        polylines.forEach { polyline ->
            val points = polyline.points.map { point ->
                val (px, py) = TileMath.latLonToPixel(point.lat, point.lon, currentZoom)
                Offset((px - originX).toFloat(), (py - originY).toFloat())
            }
            drawPoints(points = points, color = Color.Magenta, pointMode = androidx.compose.ui.graphics.PointMode.Lines)
        }
    }
}

/**
 * Rescales [panOffset] when changing zoom level so the currently visible
 * centre of the map stays in place instead of snapping back to [center].
 */
private fun rescalePanOffset(
    panOffset: Offset,
    oldZoom: Int,
    newZoom: Int,
    center: LatLon,
): Offset {
    val scale = 2.0.pow(newZoom - oldZoom).toFloat()
    val (cx, cy) = TileMath.latLonToPixel(center.lat, center.lon, oldZoom)
    val (cxNew, cyNew) = TileMath.latLonToPixel(center.lat, center.lon, newZoom)

    val visibleCx = cx.toFloat() + panOffset.x
    val visibleCy = cy.toFloat() + panOffset.y

    return Offset(
        visibleCx * scale - cxNew.toFloat(),
        visibleCy * scale - cyNew.toFloat(),
    )
}